import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Paintbrush, Eraser, RotateCcw, Check, X, Minus, Plus } from 'lucide-react';

/**
 * MaskDrawingCanvas Component
 * Allows users to paint a mask over an image for inpainting operations.
 *
 * @param {Object} props
 * @param {string} props.imageUrl - The image URL to overlay the mask on
 * @param {function} props.onMaskComplete - Callback with mask base64 when done: (maskBase64) => void
 * @param {function} props.onCancel - Callback when user cancels
 * @param {number} [props.initialBrushSize=40] - Initial brush size in pixels
 */
const MaskDrawingCanvas = ({
  imageUrl,
  onMaskComplete,
  onCancel,
  initialBrushSize = 40,
  initialPrompt = '',
  initialMode = 'remove'
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const drawingAreaRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(initialBrushSize);
  const [tool, setTool] = useState('brush'); // 'brush' or 'eraser'
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [showCursor, setShowCursor] = useState(false);
  const [prompt, setPrompt] = useState(initialPrompt);
  const [mode, setMode] = useState(initialMode);

  // Load image and set canvas size
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const container = drawingAreaRef.current;
      if (!container) return;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight - 40;

      const imgAspect = img.width / img.height;
      const containerAspect = containerWidth / containerHeight;

      let width, height;
      if (imgAspect > containerAspect) {
        width = Math.min(containerWidth, img.width);
        height = width / imgAspect;
      } else {
        height = Math.min(containerHeight, img.height);
        width = height * imgAspect;
      }

      setCanvasSize({ width, height, originalWidth: img.width, originalHeight: img.height });
      setImageLoaded(true);
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // Initialize canvas when size is set
  useEffect(() => {
    if (!canvasSize.width || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Set canvas size
    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;

    // Clear canvas (transparent)
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, [canvasSize]);

  const getCanvasCoords = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    return { x, y };
  }, []);

  const draw = useCallback((x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.globalCompositeOperation = tool === 'eraser' ? 'destination-out' : 'source-over';
    ctx.beginPath();
    ctx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 0, 0, 0.5)'; // Semi-transparent red for visibility
    ctx.fill();
  }, [brushSize, tool]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDrawing(true);
    const { x, y } = getCanvasCoords(e);
    draw(x, y);
  }, [getCanvasCoords, draw]);

  const handleMouseMove = useCallback((e) => {
    const { x, y } = getCanvasCoords(e);
    setCursorPos({ x, y });

    if (!isDrawing) return;
    draw(x, y);
  }, [isDrawing, getCanvasCoords, draw]);

  const handleMouseUp = useCallback(() => {
    setIsDrawing(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setShowCursor(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowCursor(false);
    setIsDrawing(false);
  }, []);

  // Touch events
  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    setIsDrawing(true);
    const { x, y } = getCanvasCoords(e);
    draw(x, y);
  }, [getCanvasCoords, draw]);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const { x, y } = getCanvasCoords(e);
    draw(x, y);
  }, [isDrawing, getCanvasCoords, draw]);

  const handleTouchEnd = useCallback(() => {
    setIsDrawing(false);
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  const handleComplete = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Create a new canvas at original image size for the mask
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = canvasSize.originalWidth;
    maskCanvas.height = canvasSize.originalHeight;
    const maskCtx = maskCanvas.getContext('2d');

    // Fill with black (areas to keep)
    maskCtx.fillStyle = 'black';
    maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);

    // Draw the painted areas in white (areas to edit)
    // Scale up from display size to original size
    const scaleX = canvasSize.originalWidth / canvasSize.width;
    const scaleY = canvasSize.originalHeight / canvasSize.height;

    // Get the current canvas image data
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    // Create scaled mask
    for (let y = 0; y < maskCanvas.height; y++) {
      for (let x = 0; x < maskCanvas.width; x++) {
        const srcX = Math.floor(x / scaleX);
        const srcY = Math.floor(y / scaleY);
        const srcIndex = (srcY * canvas.width + srcX) * 4;
        const alpha = imageData.data[srcIndex + 3];

        if (alpha > 0) {
          // This pixel was painted - mark as white (area to edit)
          maskCtx.fillStyle = 'white';
          maskCtx.fillRect(x, y, 1, 1);
        }
      }
    }

    // Export as base64
    const maskBase64 = maskCanvas.toDataURL('image/png');
    onMaskComplete(maskBase64, prompt, mode);
  }, [canvasSize, onMaskComplete, prompt, mode]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        zIndex: 10001,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        overflow: 'hidden'
      }}
    >
      {/* Top Refinement Panel */}
      <div style={{
        flexShrink: 0,
        padding: 'var(--space-4)',
        display: 'flex',
        justifyContent: 'center',
        zIndex: 10
      }}>
        <div style={{
          background: 'linear-gradient(180deg, rgba(30,30,40,0.95) 0%, rgba(20,20,30,0.9) 100%)',
          backdropFilter: 'blur(12px)',
          color: 'white',
          padding: 'var(--space-4) var(--space-6)',
          borderRadius: 'var(--radius-lg)',
          fontSize: 'var(--font-size-sm)',
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          border: '1px solid rgba(255,255,255,0.15)',
          width: '100%',
          maxWidth: '600px',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)'
        }}>
          <div style={{ fontWeight: 600, fontSize: 'var(--font-size-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)' }}>
            <Paintbrush size={18} />
            Masked Edit Refinement
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'center' }}>
            <button
              onClick={() => setMode('remove')}
              style={{
                padding: '6px 12px',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: mode === 'remove' ? 'var(--color-primary)' : 'rgba(255,255,255,0.1)',
                color: 'white',
                fontSize: '12px',
                cursor: 'pointer',
                fontWeight: 500,
                transition: 'all 0.2s'
              }}
            >
              Remove Content
            </button>
            <button
              onClick={() => setMode('replace')}
              style={{
                padding: '6px 12px',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: mode === 'replace' ? 'var(--color-primary)' : 'rgba(255,255,255,0.1)',
                color: 'white',
                fontSize: '12px',
                cursor: 'pointer',
                fontWeight: 500,
                transition: 'all 0.2s'
              }}
            >
              Replace/Add Content
            </button>
          </div>

          <div style={{ position: 'relative' }}>
            <textarea
              placeholder={mode === 'remove' ? "Describe background to fill (optional)..." : "What should appear in the painted area?"}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              style={{
                width: '100%',
                minHeight: '60px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 'var(--radius-md)',
                color: 'white',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--font-size-sm)',
                outline: 'none',
                resize: 'none'
              }}
            />
          </div>

          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
            Paint over the areas you want to modify, then click Done
          </div>
        </div>
      </div>

      {/* Drawing Area (Flex-grow) */}
      <div
        ref={drawingAreaRef}
        style={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          padding: 'var(--space-2)'
        }}
      >
        {/* Canvas container */}
        <div style={{ position: 'relative' }}>
          {/* Background image */}
          {imageLoaded && (
            <img
              src={imageUrl}
              alt="Source"
              style={{
                width: canvasSize.width,
                height: canvasSize.height,
                objectFit: 'contain',
                borderRadius: 'var(--radius-md)',
                pointerEvents: 'none'
              }}
              crossOrigin="anonymous"
            />
          )}

          {/* Drawing canvas overlay */}
          <canvas
            ref={canvasRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              cursor: 'none',
              borderRadius: 'var(--radius-md)'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          />

          {/* Custom cursor */}
          {showCursor && (
            <div
              style={{
                position: 'absolute',
                left: cursorPos.x - brushSize / 2,
                top: cursorPos.y - brushSize / 2,
                width: brushSize,
                height: brushSize,
                border: `2px solid ${tool === 'eraser' ? '#fff' : '#ff0000'}`,
                borderRadius: '50%',
                pointerEvents: 'none',
                backgroundColor: tool === 'eraser' ? 'rgba(255,255,255,0.2)' : 'rgba(255,0,0,0.2)'
              }}
            />
          )}
        </div>
      </div>

      {/* Bottom Controls */}
      <div style={{
        flexShrink: 0,
        padding: 'var(--space-6) var(--space-4)',
        display: 'flex',
        justifyContent: 'center',
        background: 'linear-gradient(0deg, rgba(0,0,0,0.5) 0%, transparent 100%)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-4)',
          background: 'rgba(20,20,30,0.95)',
          backdropFilter: 'blur(8px)',
          padding: 'var(--space-3) var(--space-4)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
        }}>
          {/* Tool selection */}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              onClick={() => setTool('brush')}
              style={{
                padding: 'var(--space-2)',
                background: tool === 'brush' ? 'var(--color-primary)' : 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Brush (paint areas to edit)"
            >
              <Paintbrush size={20} />
            </button>
            <button
              onClick={() => setTool('eraser')}
              style={{
                padding: 'var(--space-2)',
                background: tool === 'eraser' ? 'var(--color-primary)' : 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Eraser (remove painted areas)"
            >
              <Eraser size={20} />
            </button>
          </div>

          {/* Brush size */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'white' }}>
            <button
              onClick={() => setBrushSize(Math.max(10, brushSize - 10))}
              style={{
                padding: 'var(--space-1)',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                color: 'white',
                cursor: 'pointer',
                display: 'flex'
              }}
            >
              <Minus size={16} />
            </button>
            <span style={{ fontSize: 'var(--font-size-sm)', minWidth: '60px', textAlign: 'center' }}>
              {brushSize}px
            </span>
            <button
              onClick={() => setBrushSize(Math.min(200, brushSize + 10))}
              style={{
                padding: 'var(--space-1)',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                color: 'white',
                cursor: 'pointer',
                display: 'flex'
              }}
            >
              <Plus size={16} />
            </button>
          </div>

          {/* Clear button */}
          <button
            onClick={clearCanvas}
            style={{
              padding: 'var(--space-2)',
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1)'
            }}
            title="Clear all"
          >
            <RotateCcw size={18} />
            <span style={{ fontSize: 'var(--font-size-sm)' }}>Clear</span>
          </button>

          {/* Divider */}
          <div style={{ width: '1px', height: '24px', background: 'rgba(255,255,255,0.2)' }} />

          {/* Cancel button */}
          <button
            onClick={onCancel}
            style={{
              padding: 'var(--space-2) var(--space-3)',
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1)'
            }}
          >
            <X size={18} />
            Cancel
          </button>

          {/* Done button */}
          <button
            onClick={handleComplete}
            style={{
              padding: 'var(--space-2) var(--space-3)',
              background: 'var(--color-primary)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              fontWeight: 600
            }}
          >
            <Check size={18} />
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaskDrawingCanvas;
